/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When } from "react-if";
import { Box, FlexContainer } from "@nordcloud/gnui";
import {
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
  useResourcesNewQuery,
} from "~/generated/graphql";
import {
  ErrorMessage,
  ListLoader,
  LoaderWrap,
  PaginationState,
} from "~/components";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateFilters,
  useEstateSort,
  useFilteredQueryState,
} from "~/hooks";
import { isNotEmpty } from "~/tools";
import { ACCOUNT_DETAILS_ESTATE_COLUMNS } from "~/views/accounts/AccountDetails/Tabs/columns";
import { mapEstateRecord } from "~/views/accounts/AccountDetails/Tabs/mapping";
import { ResourcesList } from "~/views/accounts/components/ResourcesList";
import { CommonListProps } from "~/views/accounts/components/types";
import {
  EstateActiveFilters,
  EstateFiltersToggle,
  EstateSearchBox,
  EstateSort,
} from "~/views/estate/components";
import { convertTags } from "~/views/estate/helpers";
import { FiltersWrapper, SearchWrapper } from "./styles";

type Props = {
  projectResourceName: string;
  serviceType?: string;
  cloudAccountId: string;
};

export function GCPServicesList({
  projectResourceName,
  serviceType,
  cloudAccountId,
}: Props) {
  const scrollToRef = React.useRef<HTMLDivElement>(null);

  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page,
    limit,
    query,
    field,
    order,
    tags,
    ...rawFilters
  }: EstateQueryState = state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const filters = {
    ...rawFilters,
    typeSubtype: getRelatedResourcesTypes(serviceType),
    tags: convertTags(tags),
  };

  const { data, loading, error } = useResourcesNewQuery({
    variables: {
      limit,
      page,
      sort: {
        field: sortField,
        order: sortDirection,
      },
      filter: {
        ...filters,
        projectId: [projectResourceName],
        account: [cloudAccountId],
      },
      query,
    },
  });

  const { currency } = useCurrency();

  const [searchInput, setSearchInput] = React.useState(query);

  const rows = React.useMemo(
    () =>
      data?.estateV2?.resources?.map((record) =>
        mapEstateRecord(record, currency)
      ) ?? [],
    [data, currency]
  );

  const columns = React.useMemo(() => ACCOUNT_DETAILS_ESTATE_COLUMNS, []);

  const hasResources = isNotEmpty(rows);

  const estateCount = data?.estateV2?.count ?? 0;
  const showPagination = estateCount > 0;

  const { activeFilters, removeFields, clearFilters, removeFilterValue } =
    useEstateFilters(filters);
  const showFilters = isNotEmpty(activeFilters);

  const commonListProps: CommonListProps = {
    hasResources,
    data,
    columns,
    rows,
    showPagination,
    estateCount,
  };

  return (
    <div ref={scrollToRef}>
      <ErrorMessage
        error={error}
        status={hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger}
      />
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between" alignItems="center">
          <EstateSort
            sortField={sortField}
            sortDirection={sortDirection}
            onSortSelect={onSortSelect}
            onSortOrderChange={onSortOrderChange}
          />
          <SearchWrapper>
            <EstateSearchBox
              searchInput={searchInput}
              setSearchInput={setSearchInput}
            />
          </SearchWrapper>
          <EstateFiltersToggle
            isAccount
            allowSavingFilters={false}
            onApply={() => setSearchInput(query ?? "")}
          />
        </FlexContainer>
        <When condition={showFilters}>
          <FiltersWrapper>
            <EstateActiveFilters
              filters={activeFilters}
              allowSavingFilters={false}
              onRemoveKey={removeFields}
              onClear={clearFilters}
              onRemoveValue={(itemName, index) =>
                removeFilterValue(itemName as keyof EstateV2Filter, index)
              }
            />
          </FiltersWrapper>
        </When>
      </Box>
      <LoaderWrap isBackground loading={loading} Component={ListLoader}>
        <ResourcesList metadata={commonListProps} />
      </LoaderWrap>
    </div>
  );
}

function getRelatedResourcesTypes(serviceType?: string): string[] | undefined {
  switch (serviceType) {
    case SERVICE_TYPE.BIG_QUERY:
    case SERVICE_TYPE.BIG_QUERY_BI:
    case SERVICE_TYPE.BIG_QUERY_RESERVATION:
    case SERVICE_TYPE.BIG_QUERY_STORAGE:
      return ["bigquery.googleapis.com"];
    case SERVICE_TYPE.BIG_TABLE:
      return ["bigtable.googleapis.com"];
    case SERVICE_TYPE.DNS:
      return ["dns.googleapis.com"];
    case SERVICE_TYPE.FUNCTIONS:
      return ["cloudfunctions.googleapis.com"];
    case SERVICE_TYPE.PUB_SUB:
      return ["pubsub.googleapis.com"];
    case SERVICE_TYPE.SQL:
      return ["cloudsql.googleapis.com"];
    case SERVICE_TYPE.STORAGE:
      return ["storage.googleapis.com"];
    case SERVICE_TYPE.COMPUTE_ENGINE:
      return ["compute.googleapis.com"];
    case SERVICE_TYPE.KUBERNETES_ENGINE:
      return ["container.googleapis.com"];
    default:
      return ["service"];
  }
}

export enum SERVICE_TYPE {
  BIG_QUERY = "bigquery",
  BIG_QUERY_BI = "bigquery-bi-engine",
  BIG_QUERY_RESERVATION = "bigquery-reservation-api",
  BIG_QUERY_STORAGE = "bigquery-storage-api",
  BIG_TABLE = "cloud-bigtable",
  DNS = "cloud-dns",
  FUNCTIONS = "cloud-functions",
  PUB_SUB = "cloud-pub-sub",
  SQL = "cloud-sql",
  STORAGE = "cloud-storage",
  COMPUTE_ENGINE = "compute-engine",
  KUBERNETES_ENGINE = "kubernetes-engine",
}

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = EstateV2Filter &
  PaginationState &
  SortingProp & {
    tags: string[];
    query: string;
  };
